import { useCallback, useEffect, useRef, useState } from 'react';
import { localPoint } from '@visx/event';
import type { AxisScale } from '@visx/xychart';

/**
 * Hook that manages drag state and basic event handlers for drag-to-zoom functionality.
 * Handles mouse/pointer events and tracks selection coordinates.
 *
 * Uses window-level event listeners during drag to continue tracking even when
 * the cursor leaves the chart area, providing a better drag-to-zoom experience.
 *
 * @param disableDragToZoom - Whether drag-to-zoom is disabled
 * @param xScale - The x-axis scale (required for drag handling)
 * @param onDragStart - Callback when drag starts
 * @param onDragEnd - Callback when drag ends, receives current coordinates directly
 *                    to avoid stale ref issues with useEffect-based updates
 */
export function useDragState(
  disableDragToZoom: boolean,
  xScale: AxisScale | undefined,
  onDragStart: () => void,
  onDragEnd?: (startX: number | null, endX: number | null) => void,
) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectionStartX, setSelectionStartX] = useState<number | null>(null);
  const [selectionEndX, setSelectionEndX] = useState<number | null>(null);

  // Store reference to the SVG element for coordinate conversion during window events
  const svgRef = useRef<SVGSVGElement | null>(null);

  // Refs to track current coordinates synchronously (avoids stale values from useEffect)
  // These are updated immediately when state changes, before React's render cycle
  const selectionStartXRef = useRef<number | null>(null);
  const selectionEndXRef = useRef<number | null>(null);

  // Ref to track if drag should complete (used by window mouseup)
  const onDragEndRef = useRef(onDragEnd);
  useEffect(() => {
    onDragEndRef.current = onDragEnd;
  }, [onDragEnd]);

  const resetDragState = useCallback(() => {
    setIsDragging(false);
    setSelectionStartX(null);
    setSelectionEndX(null);
    selectionStartXRef.current = null;
    selectionEndXRef.current = null;
  }, []);

  // Attach window-level event listeners when dragging to continue tracking
  // even when cursor leaves the chart area
  useEffect(() => {
    if (!isDragging || disableDragToZoom) {
      return;
    }

    const handleWindowMouseUp = () => {
      // Pass current coordinates directly to avoid stale ref issues
      // Refs are updated synchronously, so they always have the latest values
      const endDrag = onDragEndRef.current;
      endDrag?.(selectionStartXRef.current, selectionEndXRef.current);
      if (endDrag) {
        resetDragState();
      }
    };

    const handleWindowMouseMove = (event: MouseEvent) => {
      // Convert window coordinates to SVG coordinates
      const svg = svgRef.current;
      if (!svg) {
        return;
      }

      const point = svg.createSVGPoint();
      point.x = event.clientX;
      point.y = event.clientY;

      const ctm = svg.getScreenCTM();
      if (!ctm) {
        return;
      }

      const svgPoint = point.matrixTransform(ctm.inverse());
      // Update both ref and state - ref for immediate access, state for React rendering
      selectionEndXRef.current = svgPoint.x;
      setSelectionEndX(svgPoint.x);
    };

    window.addEventListener('mouseup', handleWindowMouseUp);
    window.addEventListener('mousemove', handleWindowMouseMove);

    return () => {
      window.removeEventListener('mouseup', handleWindowMouseUp);
      window.removeEventListener('mousemove', handleWindowMouseMove);
    };
  }, [isDragging, disableDragToZoom, resetDragState]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      if (disableDragToZoom || !xScale) {
        return;
      }

      const point = localPoint(event);
      if (!point) {
        return;
      }

      // Store SVG element reference for coordinate conversion during window events
      svgRef.current = event.currentTarget;

      // Hide tooltip when starting to drag
      onDragStart();

      // Update both refs and state
      selectionStartXRef.current = point.x;
      selectionEndXRef.current = point.x;
      setIsDragging(true);
      setSelectionStartX(point.x);
      setSelectionEndX(point.x);
    },
    [disableDragToZoom, xScale, onDragStart],
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      if (disableDragToZoom || !isDragging) {
        return;
      }

      const point = localPoint(event);
      if (!point) {
        return;
      }

      // Update both ref and state
      selectionEndXRef.current = point.x;
      setSelectionEndX(point.x);
    },
    [disableDragToZoom, isDragging],
  );

  // No longer cancel drag on mouse leave - window listeners will handle completion
  const handleMouseLeave = useCallback(() => {
    // Intentionally empty - drag continues via window event listeners
    // This allows users to drag even when cursor leaves the chart area
  }, []);

  // Coordinate-based handlers that accept SVG coordinates directly (for use with visx event emitter)
  const handlePointerDownWithCoords = useCallback(
    (svgPoint: { x: number; y: number }) => {
      if (disableDragToZoom || !xScale) {
        return;
      }

      // Hide tooltip when starting to drag
      onDragStart();

      // Update both refs and state
      selectionStartXRef.current = svgPoint.x;
      selectionEndXRef.current = svgPoint.x;
      setIsDragging(true);
      setSelectionStartX(svgPoint.x);
      setSelectionEndX(svgPoint.x);
    },
    [disableDragToZoom, xScale, onDragStart],
  );

  const handlePointerMoveWithCoords = useCallback(
    (svgPoint: { x: number; y: number }) => {
      if (disableDragToZoom || !isDragging) {
        return;
      }

      // Update both ref and state
      selectionEndXRef.current = svgPoint.x;
      setSelectionEndX(svgPoint.x);
    },
    [disableDragToZoom, isDragging],
  );

  return {
    isDragging,
    selectionStartX,
    selectionEndX,
    handleMouseDown,
    handleMouseMove,
    handleMouseLeave,
    handlePointerDownWithCoords,
    handlePointerMoveWithCoords,
    resetDragState,
  };
}
